import { datadogRum } from '@datadog/browser-rum';
import { useLayoutEffect } from 'react';
import PendingCookies from '~/contracts/common/PendingCookies';
import BrowserCookies from '~/utilities/BrowserCookies';

// Module-scoped high-water marks: persist across SPA navigations,
// reset on hard refresh (same lifecycle as Router Cache).
// Tracks the most recent generatedAt value processed per location.
const lastProcessedByLocation = new Map<string, number>();

// Exported for testing
export function resetLastProcessedAt() {
  lastProcessedByLocation.clear();
}

function convertExpiresToDays(expires: Date): number {
  const now = new Date();
  const diffInMs = expires.getTime() - now.getTime();
  const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

  return Math.max(0, diffInDays);
}

function convertMaxAgeToDays(maxAge: number): number {
  return maxAge / (60 * 60 * 24);
}

function buildCookieOptions(options: any) {
  const cookieOptions: any = { httpOnly: false };

  if (options.expires instanceof Date) {
    cookieOptions.expires = convertExpiresToDays(options.expires);
  } else if (typeof options.expires === 'number') {
    cookieOptions.expires = options.expires;
  } else if (typeof options.maxAge === 'number') {
    cookieOptions.expires = convertMaxAgeToDays(options.maxAge);
  } else if (typeof options['Max-Age'] === 'number') {
    cookieOptions.expires = convertMaxAgeToDays(options['Max-Age']);
  }

  Object.assign(cookieOptions, {
    ...(options.path && { path: options.path }),
    ...(options.domain && { domain: options.domain }),
    ...(options.secure !== undefined && { secure: options.secure }),
    ...(options.sameSite && { sameSite: options.sameSite })
  });

  return cookieOptions;
}

export default function useSetPendingCookies(
  pendingCookies: PendingCookies,
  generatedAt: number,
  location: string
) {
  useLayoutEffect(() => {
    if (!pendingCookies || pendingCookies.length === 0) return;

    const cookieNames = pendingCookies.map(({ name }) => name);
    const lastProcessedAt = lastProcessedByLocation.get(location) ?? -1;

    const currentValues: Record<string, string | undefined> =
      cookieNames.reduce<Record<string, string | undefined>>((acc, name) => {
        acc[name] = BrowserCookies.get(name);

        return acc;
      }, {});

    const pendingValues: Record<string, string> = pendingCookies.reduce<
      Record<string, string>
    >((acc, { name, value }) => {
      acc[name] = value;

      return acc;
    }, {});

    const wouldOverwrite = Object.keys(currentValues).some(
      k => currentValues[k] !== pendingValues[k]
    );

    // Staleness guard: skip if this payload is the same age or older than
    // the most recent payload we've processed for this location.
    // Uses <= so that cached Router Cache replays (which carry the original
    // generatedAt) are always blocked. Per-location tracking ensures that
    // multiple PendingCookiesSetter instances from the same render cycle
    // never block each other.
    if (generatedAt <= lastProcessedAt) {
      datadogRum.addAction('pending-cookies-skipped', {
        location,
        wouldOverwrite,
        generatedAt,
        staleDelta: lastProcessedAt - generatedAt,
        cookieNames
      });

      return;
    }

    lastProcessedByLocation.set(location, generatedAt);

    // Write decoded values to match the geolocation API's raw cookie convention.
    // GeneralizedCookieClient writes unencoded values (e.g., "New York") via
    // document.cookie. js-cookie encodes by default, which would overwrite
    // "New York" as "New%20York". Skipping encoding here keeps both paths consistent.
    const rawWriter = BrowserCookies.withConverter({
      write: value => String(value)
    });

    pendingCookies.forEach(({ name, value, options = {} }) => {
      const cookieOptions = buildCookieOptions(options);
      rawWriter.set(name, value, cookieOptions);
    });

    datadogRum.addAction('pending-cookies-written', {
      location,
      wouldOverwrite,
      generatedAt,
      cookieNames
    });
  }, [pendingCookies, generatedAt, location]);
}
